import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, getQueryFn, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/shared/page-header";
import { SandboxedHtml } from "@/components/shared/sandboxed-html";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import type {
  EmailLayoutDetail,
  EmailLayoutUpdateInput,
  EmailTemplateDetail,
  EmailTemplatePreviewResult,
  EmailTemplateSummary,
  EmailTemplateUpdateInput,
  PlaceholderDefinition,
} from "@shared/contracts/email-templates";
import {
  Eye,
  Mail,
  RotateCcw,
  Save,
  Send,
  Sparkles,
} from "lucide-react";

function groupPlaceholders(placeholders: PlaceholderDefinition[]) {
  return placeholders.reduce<Record<string, PlaceholderDefinition[]>>(
    (groups, placeholder) => {
      groups[placeholder.group] = groups[placeholder.group] || [];
      groups[placeholder.group].push(placeholder);
      return groups;
    },
    {},
  );
}

export default function EmailTemplatesPage() {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [editor, setEditor] = useState<EmailTemplateUpdateInput | null>(null);
  const [layoutEditor, setLayoutEditor] = useState<EmailLayoutUpdateInput | null>(
    null,
  );
  const [preview, setPreview] = useState<EmailTemplatePreviewResult | null>(null);
  const [testAddress, setTestAddress] = useState("");
  const [testDialogOpen, setTestDialogOpen] = useState(false);

  const { data: templates, isLoading } = useQuery<EmailTemplateSummary[]>({
    queryKey: ["/api/email-templates"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: layout } = useQuery<EmailLayoutDetail>({
    queryKey: ["/api/email-templates/layout"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: templateDetail } = useQuery<EmailTemplateDetail>({
    queryKey: ["/api/email-templates", selectedKey],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: Boolean(selectedKey),
  });

  useEffect(() => {
    if (!selectedKey && templates?.length) {
      setSelectedKey(templates[0].key);
    }
  }, [selectedKey, templates]);

  useEffect(() => {
    if (templateDetail) {
      setEditor({
        subjectTemplate: templateDetail.subjectTemplate,
        preheaderTemplate: templateDetail.preheaderTemplate,
        htmlTemplate: templateDetail.htmlTemplate,
        textTemplate: templateDetail.textTemplate,
      });
    }
  }, [templateDetail]);

  useEffect(() => {
    if (layout) {
      setLayoutEditor({
        htmlShellTemplate: layout.htmlShellTemplate,
        textShellTemplate: layout.textShellTemplate,
      });
    }
  }, [layout]);

  const filteredTemplates = useMemo(() => {
    if (!templates) return [];
    if (!search.trim()) return templates;
    const term = search.toLowerCase();
    return templates.filter(
      (template) =>
        template.key.toLowerCase().includes(term) ||
        template.name.toLowerCase().includes(term) ||
        template.category.toLowerCase().includes(term),
    );
  }, [search, templates]);

  const previewMutation = useMutation({
    mutationFn: async () => {
      if (!selectedKey || !editor) return null;
      const res = await apiRequest("POST", `/api/email-templates/${selectedKey}/preview`, {
        draft: editor,
        layoutDraft: layoutEditor ?? undefined,
      });
      return (await res.json()) as EmailTemplatePreviewResult;
    },
    onSuccess: (result) => {
      setPreview(result);
    },
    onError: (error: Error) => {
      toast({
        title: "Preview failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const saveTemplateMutation = useMutation({
    mutationFn: async () => {
      if (!selectedKey || !editor) return null;
      const res = await apiRequest("PATCH", `/api/email-templates/${selectedKey}`, editor);
      return (await res.json()) as EmailTemplateDetail;
    },
    onSuccess: (result) => {
      if (!result) return;
      queryClient.invalidateQueries({ queryKey: ["/api/email-templates"] });
      queryClient.invalidateQueries({
        queryKey: ["/api/email-templates", selectedKey],
      });
      toast({ title: "Template saved" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to save template",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resetTemplateMutation = useMutation({
    mutationFn: async () => {
      if (!selectedKey) return null;
      const res = await apiRequest("POST", `/api/email-templates/${selectedKey}/reset`);
      return (await res.json()) as EmailTemplateDetail;
    },
    onSuccess: (result) => {
      if (!result) return;
      setEditor({
        subjectTemplate: result.subjectTemplate,
        preheaderTemplate: result.preheaderTemplate,
        htmlTemplate: result.htmlTemplate,
        textTemplate: result.textTemplate,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/email-templates"] });
      queryClient.invalidateQueries({
        queryKey: ["/api/email-templates", selectedKey],
      });
      toast({ title: "Template reset to defaults" });
    },
    onError: (error: Error) => {
      toast({
        title: "Reset failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const saveLayoutMutation = useMutation({
    mutationFn: async () => {
      if (!layoutEditor) return null;
      const res = await apiRequest("PATCH", "/api/email-templates/layout", layoutEditor);
      return (await res.json()) as EmailLayoutDetail;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/email-templates/layout"] });
      toast({ title: "Layout saved" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to save layout",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const sendTestMutation = useMutation({
    mutationFn: async () => {
      if (!selectedKey || !editor) return null;
      const res = await apiRequest(
        "POST",
        `/api/email-templates/${selectedKey}/test-send`,
        {
          toEmail: testAddress,
          draft: editor,
          layoutDraft: layoutEditor ?? undefined,
        },
      );
      return res.json();
    },
    onSuccess: () => {
      setTestDialogOpen(false);
      setTestAddress("");
      queryClient.invalidateQueries({ queryKey: ["/api/billing/email-logs"] });
      toast({ title: "Test email sent" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to send test email",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const placeholderGroups = useMemo(
    () => groupPlaceholders(templateDetail?.placeholders ?? []),
    [templateDetail?.placeholders],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Email Templates"
        description="Preview, edit, and test every system email template."
        action={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => previewMutation.mutate()}
              disabled={!selectedKey || !editor || previewMutation.isPending}
            >
              <Eye className="mr-2 h-4 w-4" />
              Preview
            </Button>
            <Button
              variant="outline"
              onClick={() => setTestDialogOpen(true)}
              disabled={!selectedKey || !editor}
            >
              <Send className="mr-2 h-4 w-4" />
              Send Test
            </Button>
          </div>
        }
      />

      <Tabs defaultValue="templates" className="space-y-6">
        <TabsList>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="layout">Shared Layout</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-6">
          <div className="grid gap-6 xl:grid-cols-[280px_1fr_320px]">
            <Card className="min-h-[680px]">
              <CardHeader>
                <CardTitle>Template List</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  placeholder="Search templates..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <ScrollArea className="h-[560px] pr-3">
                  <div className="space-y-2">
                    {isLoading ? (
                      <p className="text-sm text-muted-foreground">Loading templates...</p>
                    ) : (
                      filteredTemplates.map((template) => (
                        <button
                          key={template.key}
                          type="button"
                          onClick={() => setSelectedKey(template.key)}
                          className={`w-full rounded-lg border p-3 text-left transition-colors ${
                            selectedKey === template.key
                              ? "border-primary bg-primary/5"
                              : "border-border hover:bg-accent"
                          }`}
                        >
                          <div className="font-medium">{template.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {template.key}
                          </div>
                          <div className="mt-2 flex gap-2">
                            <Badge variant="outline">{template.category}</Badge>
                            <Badge variant="secondary">{template.deliveryChannel}</Badge>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            <Card className="min-h-[680px]">
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle>{templateDetail?.name ?? "Select a template"}</CardTitle>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {templateDetail?.description ?? "Choose a template to edit its subject, preheader, HTML, and text bodies."}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => resetTemplateMutation.mutate()}
                      disabled={!selectedKey || resetTemplateMutation.isPending}
                    >
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Reset
                    </Button>
                    <Button
                      onClick={() => saveTemplateMutation.mutate()}
                      disabled={!selectedKey || !editor || saveTemplateMutation.isPending}
                    >
                      <Save className="mr-2 h-4 w-4" />
                      Save
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {editor ? (
                  <Tabs defaultValue="subject" className="space-y-4">
                    <TabsList className="grid w-full grid-cols-5">
                      <TabsTrigger value="subject">Subject</TabsTrigger>
                      <TabsTrigger value="preheader">Preheader</TabsTrigger>
                      <TabsTrigger value="html">HTML</TabsTrigger>
                      <TabsTrigger value="text">Text</TabsTrigger>
                      <TabsTrigger value="preview">Preview</TabsTrigger>
                    </TabsList>

                    <TabsContent value="subject" className="space-y-3">
                      <Label htmlFor="subjectTemplate">Subject template</Label>
                      <Textarea
                        id="subjectTemplate"
                        rows={6}
                        value={editor.subjectTemplate}
                        onChange={(e) =>
                          setEditor((current) =>
                            current
                              ? { ...current, subjectTemplate: e.target.value }
                              : current,
                          )
                        }
                      />
                    </TabsContent>

                    <TabsContent value="preheader" className="space-y-3">
                      <Label htmlFor="preheaderTemplate">Preheader template</Label>
                      <Textarea
                        id="preheaderTemplate"
                        rows={6}
                        value={editor.preheaderTemplate}
                        onChange={(e) =>
                          setEditor((current) =>
                            current
                              ? { ...current, preheaderTemplate: e.target.value }
                              : current,
                          )
                        }
                      />
                    </TabsContent>

                    <TabsContent value="html" className="space-y-3">
                      <Label htmlFor="htmlTemplate">HTML body</Label>
                      <Textarea
                        id="htmlTemplate"
                        rows={18}
                        value={editor.htmlTemplate}
                        onChange={(e) =>
                          setEditor((current) =>
                            current
                              ? { ...current, htmlTemplate: e.target.value }
                              : current,
                          )
                        }
                      />
                    </TabsContent>

                    <TabsContent value="text" className="space-y-3">
                      <Label htmlFor="textTemplate">Text body</Label>
                      <Textarea
                        id="textTemplate"
                        rows={18}
                        value={editor.textTemplate}
                        onChange={(e) =>
                          setEditor((current) =>
                            current
                              ? { ...current, textTemplate: e.target.value }
                              : current,
                          )
                        }
                      />
                    </TabsContent>

                    <TabsContent value="preview" className="space-y-4">
                      <Button
                        variant="outline"
                        onClick={() => previewMutation.mutate()}
                        disabled={previewMutation.isPending}
                      >
                        <Sparkles className="mr-2 h-4 w-4" />
                        Refresh Preview
                      </Button>
                      <div className="grid gap-4 lg:grid-cols-2">
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-base">Rendered HTML</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="rounded-md border p-3 text-sm">
                              <div className="font-medium">Subject</div>
                              <div>{preview?.subject ?? "Run preview to render this template."}</div>
                            </div>
                            <div className="rounded-md border p-3 text-sm">
                              <div className="font-medium">Preheader</div>
                              <div>{preview?.preheader ?? "Run preview to render this template."}</div>
                            </div>
                            {preview?.html ? (
                              <SandboxedHtml
                                html={preview.html}
                                className="max-h-[480px] overflow-auto rounded-md border p-3"
                              />
                            ) : (
                              <div className="rounded-md border p-4 text-sm text-muted-foreground">
                                No preview rendered yet.
                              </div>
                            )}
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader>
                            <CardTitle className="text-base">Rendered Text</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <pre className="max-h-[600px] overflow-auto whitespace-pre-wrap rounded-md border bg-muted/30 p-4 text-sm">
                              {preview?.text ?? "Run preview to render this template."}
                            </pre>
                          </CardContent>
                        </Card>
                      </div>
                    </TabsContent>
                  </Tabs>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Choose a template from the list to start editing.
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="min-h-[680px]">
              <CardHeader>
                <CardTitle>Placeholders</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[600px] pr-3">
                  <div className="space-y-4">
                    {Object.entries(placeholderGroups).map(([group, placeholders]) => (
                      <div key={group}>
                        <div className="mb-2 font-medium">{group}</div>
                        <div className="space-y-2">
                          {placeholders.map((placeholder) => (
                            <div key={placeholder.key} className="rounded-md border p-3 text-sm">
                              <div className="flex items-start justify-between gap-2">
                                <code className="font-medium">
                                  {placeholder.supportsRawHtml
                                    ? `{{{${placeholder.key}}}}`
                                    : `{{${placeholder.key}}}`}
                                </code>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    navigator.clipboard.writeText(
                                      placeholder.supportsRawHtml
                                        ? `{{{${placeholder.key}}}}`
                                        : `{{${placeholder.key}}}`,
                                    )
                                  }
                                >
                                  Copy
                                </Button>
                              </div>
                              <div className="mt-2 text-muted-foreground">
                                {placeholder.description}
                              </div>
                              {placeholder.exampleValue ? (
                                <div className="mt-2 text-xs text-muted-foreground">
                                  Example: {placeholder.exampleValue}
                                </div>
                              ) : null}
                            </div>
                          ))}
                        </div>
                        <Separator className="mt-4" />
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="layout">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle>Shared Email Layout</CardTitle>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Reserved tokens: <code>{`{{{bodyHtml}}}`}</code>,{" "}
                    <code>{`{{bodyText}}`}</code>, <code>{`{{preheader}}`}</code>,{" "}
                    <code>{`{{brand.name}}`}</code>,{" "}
                    <code>{`{{brand.logoUrl}}`}</code>.
                  </p>
                </div>
                <Button
                  onClick={() => saveLayoutMutation.mutate()}
                  disabled={!layoutEditor || saveLayoutMutation.isPending}
                >
                  <Save className="mr-2 h-4 w-4" />
                  Save Layout
                </Button>
              </div>
            </CardHeader>
            <CardContent className="grid gap-6 xl:grid-cols-2">
              <div className="space-y-3">
                <Label htmlFor="layoutHtml">HTML shell</Label>
                <Textarea
                  id="layoutHtml"
                  rows={20}
                  value={layoutEditor?.htmlShellTemplate ?? ""}
                  onChange={(e) =>
                    setLayoutEditor((current) =>
                      current
                        ? { ...current, htmlShellTemplate: e.target.value }
                        : current,
                    )
                  }
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="layoutText">Text shell</Label>
                <Textarea
                  id="layoutText"
                  rows={20}
                  value={layoutEditor?.textShellTemplate ?? ""}
                  onChange={(e) =>
                    setLayoutEditor((current) =>
                      current
                        ? { ...current, textShellTemplate: e.target.value }
                        : current,
                    )
                  }
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Sends the current draft template to a test inbox using sample fixture data.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="test-email">Recipient address</Label>
            <Input
              id="test-email"
              type="email"
              value={testAddress}
              onChange={(e) => setTestAddress(e.target.value)}
              placeholder="qa@example.com"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTestDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => sendTestMutation.mutate()}
              disabled={!testAddress || sendTestMutation.isPending}
            >
              <Mail className="mr-2 h-4 w-4" />
              Send Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
